import React, { useState, useEffect } from 'react';
import { BarChart3, FileText, Clock, Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import PlanVsActual from './PlanVsActual';
import AuditLogsReport from './AuditLogsReport';
import TimecardsAnalysis from './TimecardsAnalysis';
import TimecardsWeeklyTotals from './TimecardsWeeklyTotals';
import ReportHistory, { ReportHistoryItem } from '../components/ReportHistory';
import { timecardApi } from '../services/api';
import { Timecard } from '../types';

type ReportSection = 'plan-vs-actual' | 'audit-logs' | 'timecards-analysis' | 'timecards-weekly-totals';

const Reports: React.FC = () => {
  const [activeSection, setActiveSection] = useState<ReportSection>('plan-vs-actual');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [timecardsExpanded, setTimecardsExpanded] = useState(false);
  const [reportHistory, setReportHistory] = useState<ReportHistoryItem[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(true);

  // Close sidebar when window is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load report history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('reportHistory');
    if (savedHistory) {
      const parsed = JSON.parse(savedHistory);
      setReportHistory(parsed.map((r: any) => ({
        ...r,
        createdAt: new Date(r.createdAt),
        completedAt: r.completedAt ? new Date(r.completedAt) : undefined
      })));
    }
  }, []);

  // Save report history to localStorage whenever it changes
  useEffect(() => {
    if (reportHistory.length > 0) {
      localStorage.setItem('reportHistory', JSON.stringify(reportHistory));
    } else {
      // Clear localStorage if history is empty
      localStorage.removeItem('reportHistory');
    }
  }, [reportHistory]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      reportHistory.forEach(report => {
        if (report.csvUrl && report.csvUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.csvUrl);
        }
        if (report.excelUrl && report.excelUrl.startsWith('blob:')) {
          URL.revokeObjectURL(report.excelUrl);
        }
      });
    };
  }, []);

  const sidebarItems = [
    { id: 'plan-vs-actual' as ReportSection, label: 'Plan Vs Actual', icon: BarChart3, isParent: false },
    { id: 'audit-logs' as ReportSection, label: 'Audit Logs', icon: FileText, isParent: false },
  ];

  const timecardsItems = [
    { id: 'timecards-analysis' as ReportSection, label: 'Analysis', icon: BarChart3 },
    { id: 'timecards-weekly-totals' as ReportSection, label: 'Weekly Totals', icon: FileText },
  ];

  const generateTimecardAnalysisData = (timecards: Timecard[]) => {
    const headers = [
      'Employee Name', 
      'Date', 
      'Scheduled In', 
      'Scheduled Out', 
      'Actual In', 
      'Actual Out',
      'Paid In',
      'Paid Out',
      'Scheduled Hours',
      'Actual Hours',
      'Paid Hours',
      'Break Minutes',
      'Position',
      'Status'
    ];
    
    const rows: string[][] = [];
    
    timecards.forEach(timecard => {
      const employeeName = timecard.employee 
        ? `${timecard.employee.firstName} ${timecard.employee.lastName}` 
        : 'Unknown';
      
      timecard.days.forEach(day => {
        const formatTime = (dateTime: string | null | undefined) => {
          if (!dateTime) return '';
          try {
            const date = new Date(dateTime);
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          } catch {
            return dateTime;
          }
        };
        
        const formatDate = (dateStr: string) => {
          try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          } catch {
            return dateStr;
          }
        };
        
        const calculateHours = (inTime: string | null | undefined, outTime: string | null | undefined) => {
          if (!inTime || !outTime) return '0.00';
          try {
            const inDate = new Date(inTime);
            const outDate = new Date(outTime);
            const hours = (outDate.getTime() - inDate.getTime()) / (1000 * 60 * 60);
            return hours > 0 ? hours.toFixed(2) : '0.00';
          } catch {
            return '0.00';
          }
        };
        
        const scheduledHours = calculateHours(day.scheduledIn, day.scheduledOut);
        const actualHours = calculateHours(day.actualIn, day.actualOut);
        const paidHours = calculateHours(day.paidIn, day.paidOut);
        
        rows.push([
          employeeName,
          formatDate(day.date),
          formatTime(day.scheduledIn),
          formatTime(day.scheduledOut),
          formatTime(day.actualIn),
          formatTime(day.actualOut),
          formatTime(day.paidIn),
          formatTime(day.paidOut),
          scheduledHours,
          actualHours,
          paidHours,
          (day.paidBreakMinutes || day.actualBreakMinutes || day.scheduledBreakMinutes || 0).toString(),
          day.positionCode || '',
          timecard.isApproved ? 'Approved' : 'Pending'
        ]);
      });
    });
    
    return { headers, rows };
  };

  const generateWeeklyTotalsData = (timecards: Timecard[], fromWeekStart: string, toWeekStart: string) => {
    const fromDate = new Date(fromWeekStart);
    const toDate = new Date(toWeekStart);
    
    // Group timecards by employee and week
    const employeeData = new Map<number, { name: string; weeks: Map<string, number> }>();
    
    timecards.forEach(timecard => {
      if (!employeeData.has(timecard.employeeId)) {
        const employeeName = timecard.employee 
          ? `${timecard.employee.firstName} ${timecard.employee.lastName}`
          : `Employee ${timecard.employeeId}`;
        
        employeeData.set(timecard.employeeId, {
          name: employeeName,
          weeks: new Map()
        });
      }
      
      const empData = employeeData.get(timecard.employeeId)!;
      
      // Calculate which week number this is (1-13) relative to fromDate
      const timecardWeekDate = new Date(timecard.weekStartDate);
      const weekNumber = Math.floor((timecardWeekDate.getTime() - fromDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
      
      // Sum up total hours for this week from all days
      const totalHours = timecard.days?.reduce((sum, day) => sum + (day.totalHours || 0), 0) || 0;
      
      // Store with week number as key
      if (weekNumber >= 0 && weekNumber < 13) {
        empData.weeks.set(`week_${weekNumber}`, totalHours);
      }
    });
    
    // Create headers for Week 1-13 with dates in brackets ONLY for weeks in search range
    const headers = ['Employee Name'];
    
    for (let i = 0; i < 13; i++) {
      const weekDate = new Date(fromDate);
      weekDate.setDate(weekDate.getDate() + (i * 7));
      
      // Check if this week is within the selected range
      const isInRange = weekDate >= fromDate && weekDate <= toDate;
      
      if (isInRange) {
        // Show date in brackets for weeks in search range
        headers.push(`Week ${i + 1} (${weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`);
      } else {
        // No date for weeks outside search range
        headers.push(`Week ${i + 1}`);
      }
    }
    
    headers.push('Total');
    
    // Create rows with data from ALL timecards (even outside range)
    const rows = Array.from(employeeData.values()).map(empData => {
      // Generate 13 week values
      const weekValues = Array.from({ length: 13 }, (_, i) => {
        const hours = empData.weeks.get(`week_${i}`) || 0;
        return hours.toFixed(2);
      });
      
      // Calculate total
      const total = weekValues.reduce((sum, val) => sum + parseFloat(val), 0).toFixed(2);
      
      return [empData.name, ...weekValues, total];
    });
    
    // Sort by employee name
    rows.sort((a, b) => a[0].localeCompare(b[0]));
    
    return { headers, rows };
  };

  const createCSVFile = (headers: string[], rows: string[][], filename: string) => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    return { url, filename: `${filename}.csv` };
  };

  const createExcelFile = (headers: string[], rows: string[][], filename: string) => {
    // Create a proper Excel .xlsx file using the xlsx library
    // This creates a real Excel file, not CSV
    
    // Create worksheet data with headers and rows
    const wsData = [headers, ...rows];
    
    // Create a new workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Auto-size columns based on content
    const colWidths = headers.map((_, colIndex) => {
      const maxLength = Math.max(
        headers[colIndex].length,
        ...rows.map(row => String(row[colIndex] || '').length)
      );
      return { wch: Math.min(maxLength + 2, 50) }; // Max width of 50
    });
    ws['!cols'] = colWidths;
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    
    // Generate Excel file as binary
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Create blob with proper Excel MIME type
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = URL.createObjectURL(blob);
    return { url, filename: `${filename}.xlsx` };
  };

  const handleDeleteReport = (reportId: string) => {
    setReportHistory(prev => {
      const reportToDelete = prev.find(r => r.id === reportId);
      
      // Revoke blob URLs to free memory
      if (reportToDelete) {
        if (reportToDelete.csvUrl && reportToDelete.csvUrl.startsWith('blob:')) {
          URL.revokeObjectURL(reportToDelete.csvUrl);
        }
        if (reportToDelete.excelUrl && reportToDelete.excelUrl.startsWith('blob:')) {
          URL.revokeObjectURL(reportToDelete.excelUrl);
        }
      }
      
      // Remove from array
      return prev.filter(r => r.id !== reportId);
    });
  };

  const handleGenerateReport = async (request: Omit<ReportHistoryItem, 'id' | 'status' | 'createdAt' | 'reportName'>) => {
    const reportId = `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Derive report name from report type
    const reportName = request.reportType === 'timecards-analysis' 
      ? 'Timecards Analysis' 
      : 'Weekly Totals';
    
    const newReport: ReportHistoryItem = {
      id: reportId,
      reportType: request.reportType,
      reportName: reportName,
      parameters: request.parameters,
      status: 'pending',
      createdAt: new Date()
    };

    // Add to history
    setReportHistory(prev => [newReport, ...prev]);

    // Expand history panel
    setHistoryExpanded(true);

    // Start processing
    setTimeout(async () => {
      setReportHistory(prev =>
        prev.map(r =>
          r.id === reportId ? { ...r, status: 'processing' } : r
        )
      );

      try {
        let headers: string[];
        let rows: string[][];
        
        if (request.reportType === 'timecards-analysis') {
          // Fetch real timecard data for a specific week
          const allTimecards = await timecardApi.getAll(undefined, request.parameters.weekStartDate);
          
          // Filter for approved timecards only
          const timecards = allTimecards.filter(tc => tc.isApproved === true);
          
          if (!timecards || timecards.length === 0) {
            throw new Error('No approved timecard data found for the selected week');
          }
          
          const reportData = generateTimecardAnalysisData(timecards);
          headers = reportData.headers;
          rows = reportData.rows;
        } else if (request.reportType === 'timecards-weekly-totals') {
          // Fetch ALL approved timecards (not filtered by date range for data display)
          const allTimecards = await timecardApi.getAll();
          
          // Filter for approved timecards only
          const timecards = allTimecards.filter(tc => tc.isApproved === true);
          
          if (!timecards || timecards.length === 0) {
            throw new Error('No approved timecard data found');
          }
          
          // Pass the from date to determine Week 1 reference point
          const reportData = generateWeeklyTotalsData(
            timecards, 
            request.parameters.fromWeekStartDate,
            request.parameters.toWeekStartDate
          );
          headers = reportData.headers;
          rows = reportData.rows;
        } else {
          throw new Error('Unknown report type');
        }
        
        // Create download files
        const timestamp = new Date().toISOString().slice(0, 10);
        const baseFilename = `${reportName.replace(/\s+/g, '_')}_${timestamp}`;
        
        const csvFile = createCSVFile(headers, rows, baseFilename);
        const excelFile = createExcelFile(headers, rows, baseFilename);
        
        setReportHistory(prev =>
          prev.map(r =>
            r.id === reportId
              ? {
                  ...r,
                  status: 'completed',
                  completedAt: new Date(),
                  csvUrl: csvFile.url,
                  excelUrl: excelFile.url
                }
              : r
          )
        );
      } catch (error: any) {
        console.error('Error generating report:', error);
        setReportHistory(prev =>
          prev.map(r =>
            r.id === reportId
              ? {
                  ...r,
                  status: 'failed',
                  completedAt: new Date(),
                  error: error.message || 'Failed to generate report'
                }
              : r
          )
        );
      }
    }, 500);
  };

  const renderContent = () => {
    const content = (() => {
      switch (activeSection) {
        case 'plan-vs-actual':
          return <PlanVsActual />;
        case 'audit-logs':
          return <AuditLogsReport />;
        case 'timecards-analysis':
          return <TimecardsAnalysis onGenerateReport={handleGenerateReport} />;
        case 'timecards-weekly-totals':
          return <TimecardsWeeklyTotals onGenerateReport={handleGenerateReport} />;
        default:
          return <PlanVsActual />;
      }
    })();

    // Show history panel only for timecard reports
    const showHistory = activeSection.startsWith('timecards');
    
    // Filter history for the current report type only
    const filteredHistory = reportHistory.filter(report => {
      if (activeSection === 'timecards-analysis') {
        return report.reportType === 'timecards-analysis';
      } else if (activeSection === 'timecards-weekly-totals') {
        return report.reportType === 'timecards-weekly-totals';
      }
      return false;
    });

    return (
      <div className="h-full flex flex-col">
        <div className={showHistory ? 'flex-1 overflow-auto' : 'h-full'}>
          {content}
        </div>
        {showHistory && (
          <div className="border-t border-neutral-200 bg-white">
            <ReportHistory
              reports={filteredHistory}
              isExpanded={historyExpanded}
              onToggleExpanded={() => setHistoryExpanded(!historyExpanded)}
              onDeleteReport={handleDeleteReport}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full bg-neutral-50">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-neutral-200"
      >
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed md:relative inset-y-0 left-0 z-40 w-64 bg-white border-r border-neutral-200 transform transition-transform duration-200 ease-in-out flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        {/* Sidebar Header */}
        <div className="px-4 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">Reports</h2>
          <p className="text-sm text-neutral-600 mt-1">View system reports and analytics</p>
        </div>

        {/* Sidebar Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {/* Regular Menu Items */}
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}

            {/* Timecards Collapsible Menu */}
            <div className="space-y-1">
              <button
                onClick={() => setTimecardsExpanded(!timecardsExpanded)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                  activeSection.startsWith('timecards')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {timecardsExpanded ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                )}
                <Clock className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">Timecards</span>
              </button>

              {/* Timecards Sub-menu */}
              {timecardsExpanded && (
                <div className="ml-6 space-y-1 border-l-2 border-neutral-200 pl-2">
                  {timecardsItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveSection(item.id);
                          setSidebarOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm ${
                          activeSection === item.id
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto w-full">
        <div className="h-full">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Reports;
